import { FC, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import { SuccessCheckIcon, UploadArrowIcon } from './UploadProgressSnackbar.icons';
import { UploadProgressSnackbarProps } from './UploadProgressSnackbar.types';
import styles from './UploadProgressSnackbar.module.scss';

export * from './UploadProgressSnackbar.types';
export * from './UploadProgressSnackbar.icons';

export const UploadProgressSnackbar: FC<UploadProgressSnackbarProps> = ({
	open,
	phase = 'uploading',
	progress = 0,
	items,
}) => {
	const { t } = useTranslation();

	const [isRendered, setIsRendered] = useState(open);
	const [isExiting, setIsExiting] = useState(false);
	const lastItemsRef = useRef(items);
	const lastPhaseRef = useRef(phase);
	const lastProgressRef = useRef(progress);

	if (items && items.length > 0) {
		lastItemsRef.current = items;
	}
	if (open) {
		lastPhaseRef.current = phase;
		lastProgressRef.current = progress;
	}

	useEffect(() => {
		let timer: ReturnType<typeof setTimeout>;
		if (open) {
			setIsRendered(true);
			setIsExiting(false);
		} else if (isRendered) {
			setIsExiting(true);
			timer = setTimeout(() => {
				setIsRendered(false);
				setIsExiting(false);
				lastItemsRef.current = undefined;
			}, 250);
		}
		return () => {
			if (timer) clearTimeout(timer);
		};
	}, [open, isRendered]);

	if (!isRendered && !open) return null;

	const currentItems = items && items.length > 0 ? items : lastItemsRef.current;
	const currentPhase = open ? phase : lastPhaseRef.current;
	const currentProgress = open ? progress : lastProgressRef.current;

	const content = (
		<div
			className={cx(styles.snackbarWrapper, { [styles['snackbarWrapper--exiting']]: isExiting })}
			role="status"
			aria-live="polite"
		>
			{currentItems && currentItems.length > 0 ? (
				currentItems.map(item => {
					const isProcessing = item.status === 'server-processing';
					const isTranscoding = item.status === 'client-transcoding';
					const isSuccess = item.status === 'success';
					const isUploading = item.status === 'uploading';

					let statusTitle = t('uploading_media', 'Uploading media...');
					if (isProcessing) {
						statusTitle = t('processing_media', 'Processing media on server...');
					} else if (isTranscoding) {
						statusTitle = t('server_media_optimizing', 'Optimizing and converting video/photos');
					} else if (isSuccess) {
						statusTitle = t('completed', 'Completed');
					}

					return (
						<div
							key={item.id}
							className={cx(styles.snackbarCard, { [styles['snackbarCard--exiting']]: isExiting })}
						>
							<div className={styles.headerRow}>
								<div className={styles.statusContainer}>
									{isProcessing || isTranscoding ? (
										<span className={styles.spinner} />
									) : isSuccess ? (
										<div className={styles.successIcon}>
											<SuccessCheckIcon />
										</div>
									) : (
										<div className={styles.uploadIcon}>
											<UploadArrowIcon />
										</div>
									)}
									<div className={styles.textGroup}>
										<span className={styles.title} title={item.file.name}>
											{item.file.name}
										</span>
										<span className={styles.subtitle}>{statusTitle}</span>
									</div>
								</div>
								{isUploading && <span className={styles.percentage}>{item.progress}%</span>}
								{isSuccess && <span className={styles.percentage}>100%</span>}
							</div>

							{(isUploading || isSuccess) && (
								<div className={styles.progressTrack}>
									<div
										className={cx(styles.progressBar, {
											[styles['progressBar--success']]: isSuccess,
										})}
										style={{
											width: `${isSuccess ? 100 : Math.min(100, Math.max(0, item.progress))}%`,
										}}
									/>
								</div>
							)}
						</div>
					);
				})
			) : (
				<div className={cx(styles.snackbarCard, { [styles['snackbarCard--exiting']]: isExiting })}>
					<div className={styles.headerRow}>
						<div className={styles.statusContainer}>
							{currentPhase === 'processing' ? (
								<span className={styles.spinner} />
							) : (
								<div className={styles.uploadIcon}>
									<UploadArrowIcon />
								</div>
							)}
							<div className={styles.textGroup}>
								<span className={styles.title}>
									{currentPhase === 'processing'
										? t('processing_media', 'Processing media on server...')
										: t('uploading_media', 'Uploading media...')}
								</span>
								<span className={styles.subtitle}>
									{currentPhase === 'processing'
										? t('server_media_optimizing', 'Optimizing and converting video/photos')
										: t('please_wait_uploading', 'Please wait while files are being uploaded')}
								</span>
							</div>
						</div>
						{currentPhase === 'uploading' && <span className={styles.percentage}>{currentProgress}%</span>}
					</div>

					{currentPhase === 'uploading' && (
						<div className={styles.progressTrack}>
							<div
								className={styles.progressBar}
								style={{ width: `${Math.min(100, Math.max(0, currentProgress))}%` }}
							/>
						</div>
					)}
				</div>
			)}
		</div>
	);

	return typeof document !== 'undefined' ? createPortal(content, document.body) : content;
};

export default UploadProgressSnackbar;
